import type {
  BookEventBranch,
  BookEventHostData,
  BookEventUser,
  EagreementsSnapshot,
} from '../components/booking-purchase-dialog/bookEvent.types';
import { mapPriceBreakdownResponse } from './mapPriceBreakdownResponse';

type Scope = {
  $on: (event: '$destroy', callback: () => void) => void;
};

type Modal = {
  element: { modal: () => void };
  close: Promise<unknown>;
};

type BookingPurchaseHostDependencies = {
  envUrl: string;
  defaultCurrencySymbol: string;
  cardPaymentMethod: unknown;
  weekDays: Record<string, string>;
  termsMessageType: unknown;
  getCurrentUser: () => BookEventUser | null;
  getCurrentBranch: () => BookEventBranch | null;
  getCurrentBranchPenalties: () => any;
  isClassBookingEnabled: () => boolean;
  isPriceEnabled: () => boolean;
  isSizeEnabled: () => boolean;
  isTotalBookingsEnabled: () => boolean;
  isFacilityNameEnabled: () => boolean;
  isLevelEnabled: () => boolean;
  isTrainerNameEnabled: () => boolean;
  isTotalWaitingListEnabled: () => boolean;
  isShortTrainerNameEnabled: () => boolean;
  resolveRequestToken: () => string | null;
  getFlags: (variant: 'book-event' | 'book-appointment') => any;
  getEventPrice: (event: any, guests?: number, options?: any) => Promise<any>;
  getAppointmentPrice: (event: any, options: Record<string, never>) => Promise<any>;
  createBooking: (input: any) => Promise<any>;
  createAppointmentBooking: (input: any) => Promise<any>;
  isSpotBookingEnabledForEvent: (event: any) => boolean;
  showModal: (options: any) => Promise<Modal>;
  createReactDigestBridge: (scope: Scope) => (callback: () => void) => void;
  onRootEvent: (event: string, callback: () => void) => () => void;
  activateEagreements: () => void;
  deactivateEagreements: () => void;
  getEagreementsState: () => any;
  refreshEagreements: (options?: any) => Promise<unknown>;
  isMissingProfileDetailsBlockingBooking: () => boolean;
  openProfile: () => unknown;
  goToDocument: (url: string) => unknown;
  buildNotEligibleDialogData: (dismiss: () => void) => any;
  forceReloadClassesView: () => void;
  openTerms: (options: any) => void;
  getPriceBreakdown: (request: any[]) => Promise<any>;
  buildPriceBreakdown: (breakdown: any, options: any) => any;
  emitCartPriceUpdated: (breakdown: any) => void;
  triggerAnalyticsEvent: (payload: any) => void;
  trackCheckoutInitiation: () => void;
  trackBooking: () => void;
  formatUnix: (timestamp: number, format: string) => string;
  getBranchLocalTime: (timezone?: string) => string;
  startOfDayUnix: (timestamp: number, timezone?: string) => number;
  shortenTrainerName: (trainer: any) => any;
  getTrainerById: (trainerId: string) => Promise<any>;
  copyToClipboard: (value: string) => void;
  getAbsUrl: () => string;
  getWindowWidth: () => number;
  getBranchCurrencySymbol: (branch: BookEventBranch | null) => string | undefined;
  clone: <T>(value: T) => T;
  scrollIframeToTop: () => void;
  logError?: () => void;
};

type HostOptions = {
  variant?: 'book-event' | 'book-appointment';
  event: any;
  dismissModal: () => void;
  analyticsCategory?: string;
  loginAnalyticsName?: string;
  paymentOrigin?: string;
  getFlags?: () => any;
  getEventPrice?: (event: any, guests?: number, options?: any) => Promise<any>;
  createBooking?: (booking: any) => Promise<any>;
  isSpotBookingEnabledForEvent?: (event: any) => boolean;
  showBookSpot?: (bookingId: string, guests: number) => Promise<unknown>;
};

type HostSession = {
  user: BookEventUser | null;
  branch: BookEventBranch | null;
  data: BookEventHostData | null;
};

function toPriceBreakdownRequest(services: any[]) {
  return services.map((service) => ({
    service_type: service.type,
    service_id: service.service_id,
    price: service.price,
    unit_price: service.price,
    quantity: 1,
  }));
}

function hostFlags(
  options: HostOptions,
  dependencies: BookingPurchaseHostDependencies
) {
  if (options.getFlags) {
    return options.getFlags();
  }
  return dependencies.getFlags(options.variant!);
}

function snapshotEagreements(
  dependencies: BookingPurchaseHostDependencies
): EagreementsSnapshot {
  const state = dependencies.getEagreementsState();
  return {
    flagResolved: state.isFlagResolved,
    mandatoryEnabled: state.isMandatoryEnabled,
    documentsSigned: state.areDocumentsSigned,
    outstandingDocuments: state.outstandingDocuments,
    loadingOutstandingDocuments: state.loadingOutstandingDocuments,
    isMissingProfileDetails:
      dependencies.isMissingProfileDetailsBlockingBooking(),
  };
}

function portalConfig(dependencies: BookingPurchaseHostDependencies) {
  return {
    isPriceEnabled: dependencies.isPriceEnabled(),
    isSizeEnabled: dependencies.isSizeEnabled(),
    isTotalBookingsEnabled: dependencies.isTotalBookingsEnabled(),
    isFacilityNameEnabled: dependencies.isFacilityNameEnabled(),
    isLevelEnabled: dependencies.isLevelEnabled(),
    isTrainerNameEnabled: dependencies.isTrainerNameEnabled(),
    isTotalWaitingListEnabled: dependencies.isTotalWaitingListEnabled(),
    isShortTrainerNameEnabled: dependencies.isShortTrainerNameEnabled(),
    isClassBookingEnabled: dependencies.isClassBookingEnabled(),
  };
}

function paymentOrigin(options: HostOptions) {
  if (options.paymentOrigin) {
    return options.paymentOrigin;
  }
  if (options.variant === 'book-appointment') {
    return 'book-appointment';
  }
  return 'book-classes';
}

function loginAnalytics(options: HostOptions) {
  const isAppointment = options.variant === 'book-appointment';
  return {
    category:
      options.analyticsCategory ||
      (isAppointment ? 'source:appointments' : 'source:event'),
    name:
      options.loginAnalyticsName ||
      (isAppointment ? 'SHOW_BUY_MODAL_LOGGED_OUT' : 'SHOW_BOOK_MODAL_LOGGED_OUT'),
  };
}

function priceForEvent(
  options: HostOptions,
  dependencies: BookingPurchaseHostDependencies,
  request: { event: any; guests: number; priceOptions?: any }
) {
  if (options.getEventPrice) {
    return options.getEventPrice(
      request.event,
      request.guests,
      request.priceOptions
    );
  }
  if (options.variant === 'book-appointment') {
    return dependencies.getAppointmentPrice(request.event, {});
  }
  return dependencies.getEventPrice(
    request.event,
    request.guests,
    request.priceOptions
  );
}

function bookingForEvent(
  options: HostOptions,
  dependencies: BookingPurchaseHostDependencies,
  booking: any
) {
  if (options.createBooking) {
    return options.createBooking(booking);
  }
  if (options.variant === 'book-appointment') {
    return dependencies.createAppointmentBooking(booking);
  }
  return dependencies.createBooking(booking);
}

function spotBookingEnabled(
  options: HostOptions,
  dependencies: BookingPurchaseHostDependencies,
  event: any
) {
  if (options.isSpotBookingEnabledForEvent) {
    return options.isSpotBookingEnabledForEvent(event);
  }
  if (options.variant === 'book-event') {
    return dependencies.isSpotBookingEnabledForEvent(event);
  }
  return false;
}

function showClosingModal(
  dependencies: BookingPurchaseHostDependencies,
  modalOptions: any,
  onClose: (value: any) => any
) {
  return dependencies
    .showModal(modalOptions)
    .then((modal) => {
      modal.element.modal();
      return modal.close.then(onClose);
    })
    .catch(() => {
      dependencies.logError?.();
      return null;
    });
}

function showGeneralDialog(
  dependencies: BookingPurchaseHostDependencies,
  dialogData: any
) {
  void dependencies
    .showModal({
      templateUrl: 'dialogs/general-dialog/general-dialog.html',
      controller: 'GeneralDialogController',
      controllerAs: 'vm',
      inputs: { data: dialogData },
    })
    .then((modal) => modal.element.modal())
    .catch(() => dependencies.logError?.());
}

function getHostPriceBreakdown(
  dependencies: BookingPurchaseHostDependencies,
  services: any[],
  currency: string
) {
  return dependencies
    .getPriceBreakdown(toPriceBreakdownRequest(services))
    .then((quote) => {
      const breakdown = mapPriceBreakdownResponse(quote);
      const built = dependencies.buildPriceBreakdown(breakdown, {
        currency,
        services,
      });
      dependencies.emitCartPriceUpdated(breakdown);
      return built;
    });
}

function digestBridge(
  applyReactChange: (callback: () => void) => void
) {
  return <TArgs extends unknown[], TResult>(
    callback: (...args: TArgs) => TResult
  ) =>
    (...args: TArgs) => {
      let result: TResult;
      applyReactChange(() => {
        result = callback(...args);
      });
      return result!;
    };
}

function syncHostSnapshots(
  session: HostSession,
  options: HostOptions,
  dependencies: BookingPurchaseHostDependencies
) {
  session.user = dependencies.getCurrentUser();
  session.branch = dependencies.getCurrentBranch();
  if (!session.data) {
    return;
  }
  session.data.user = dependencies.clone(session.user);
  session.data.branch = session.branch;
  session.data.userToken = dependencies.resolveRequestToken();
  session.data.flags = hostFlags(options, dependencies);
  session.data.portalConfig = portalConfig(dependencies);
  session.data.penalties = dependencies.getCurrentBranchPenalties();
  session.data.eagreements = snapshotEagreements(dependencies);
}

function syncEagreementsSnapshot(
  session: HostSession,
  dependencies: BookingPurchaseHostDependencies
) {
  const snapshot = snapshotEagreements(dependencies);
  if (session.data) {
    session.data.eagreements = snapshot;
  }
  return snapshot;
}

function refreshEagreements(
  session: HostSession,
  dependencies: BookingPurchaseHostDependencies,
  refreshOptions?: unknown
) {
  return dependencies.refreshEagreements(refreshOptions).then(
    () => syncEagreementsSnapshot(session, dependencies),
    () => syncEagreementsSnapshot(session, dependencies)
  );
}

function showPaymentCollector(
  options: HostOptions,
  dependencies: BookingPurchaseHostDependencies
) {
  return showClosingModal(
    dependencies,
    {
      templateUrl: 'dialogs/payment-collector/payment-collector.html',
      controller: 'PaymentCollectorController',
      controllerAs: 'vm',
      inputs: {
        paymentMethod: dependencies.cardPaymentMethod,
        cartId: false,
        origin: paymentOrigin(options),
        hasNewPaymentMethodFeature: false,
      },
    },
    () => dependencies.clone(dependencies.getCurrentUser())
  );
}

function showLoginRegister(
  session: HostSession,
  options: HostOptions,
  dependencies: BookingPurchaseHostDependencies
) {
  dependencies.triggerAnalyticsEvent(loginAnalytics(options));
  return showClosingModal(
    dependencies,
    {
      templateUrl: 'dialogs/login-register/login-register.html',
      controller: 'LoginRegisterController',
      controllerAs: 'vm',
      inputs: { isGoingToBook: true, showRegisterForm: false },
    },
    () => {
      syncHostSnapshots(session, options, dependencies);
      return refreshEagreements(session, dependencies, { forceRefresh: true });
    }
  );
}

function showGoPayg(
  session: HostSession,
  options: HostOptions,
  dependencies: BookingPurchaseHostDependencies
) {
  return showClosingModal(
    dependencies,
    {
      templateUrl: 'dialogs/go-payg/go-payg.html',
      controller: 'GoPAYGController',
      controllerAs: 'vm',
    },
    (result) => {
      if (result?.success === true) {
        syncHostSnapshots(session, options, dependencies);
      }
    }
  );
}

function buildHostActions(
  session: HostSession,
  options: HostOptions,
  dependencies: BookingPurchaseHostDependencies,
  bridge: ReturnType<typeof digestBridge>
) {
  return {
    onClose: bridge(options.dismissModal),
    showTermsRequiredDialog: bridge(() =>
      showGeneralDialog(dependencies, {
        type: dependencies.termsMessageType,
        title: 'WEB_TERMS_AND_CONDITIONS',
        message: 'WEB_PLEASE_ACCEPT_TERMS_AND_CONDITIONS',
        buttonLabel: 'WEB_OK',
      })
    ),
    showNotEligibleDialog: bridge(() =>
      showGeneralDialog(
        dependencies,
        dependencies.buildNotEligibleDialogData(options.dismissModal)
      )
    ),
    showPaymentCollector: bridge(() =>
      showPaymentCollector(options, dependencies)
    ),
    showLoginRegister: bridge(() =>
      showLoginRegister(session, options, dependencies)
    ),
    showGoPayg: bridge(() => showGoPayg(session, options, dependencies)),
    showPaygTerms: bridge(() => dependencies.openTerms({ mode: 'payg' })),
    showBookSpot: bridge(options.showBookSpot || (() => Promise.resolve())),
    openProfile: bridge(dependencies.openProfile),
    openDocument: bridge((documentUrl: string) => {
      dependencies.goToDocument(documentUrl);
      return snapshotEagreements(dependencies);
    }),
  };
}

function buildHostData(
  session: HostSession,
  options: HostOptions,
  dependencies: BookingPurchaseHostDependencies,
  bridge: ReturnType<typeof digestBridge>
): BookEventHostData {
  const timezone = () => session.branch?.address?.timezone_id;
  return {
    envUrl: dependencies.envUrl,
    userToken: dependencies.resolveRequestToken(),
    event: options.event,
    user: dependencies.clone(session.user),
    branch: session.branch,
    penalties: dependencies.getCurrentBranchPenalties(),
    currencySymbol:
      dependencies.getBranchCurrencySymbol(session.branch) ||
      dependencies.defaultCurrencySymbol,
    weekDays: dependencies.weekDays,
    flags: hostFlags(options, dependencies),
    portalConfig: portalConfig(dependencies),
    eagreements: snapshotEagreements(dependencies),
    formatUnix: dependencies.formatUnix,
    getBranchLocalTime: () => dependencies.getBranchLocalTime(timezone()),
    shortenTrainerName: dependencies.shortenTrainerName,
    getTrainerById: dependencies.getTrainerById,
    getEventPrice: (event, guests, priceOptions) =>
      priceForEvent(options, dependencies, { event, guests, priceOptions }),
    createBooking: (booking) =>
      bookingForEvent(options, dependencies, booking),
    getPriceBreakdown: (services, currency) =>
      getHostPriceBreakdown(dependencies, services, currency),
    refreshEagreements: (refreshOptions) =>
      refreshEagreements(session, dependencies, refreshOptions),
    isSpotBookingEnabledForEvent: (event) =>
      spotBookingEnabled(options, dependencies, event),
    trackEvent: dependencies.triggerAnalyticsEvent,
    trackCheckoutInitiation: dependencies.trackCheckoutInitiation,
    trackBooking: dependencies.trackBooking,
    copyToClipboard: dependencies.copyToClipboard,
    getAbsUrl: dependencies.getAbsUrl,
    getWindowWidth: dependencies.getWindowWidth,
    startOfDayUnix: (timestamp) =>
      dependencies.startOfDayUnix(timestamp, timezone()),
    forceReloadClassesView: dependencies.forceReloadClassesView,
    scrollIframeToTop: dependencies.scrollIframeToTop,
    ...buildHostActions(session, options, dependencies, bridge),
  };
}

function subscribeHost(
  scope: Scope,
  session: HostSession,
  options: HostOptions,
  dependencies: BookingPurchaseHostDependencies
) {
  const unbindUserChange = dependencies.onRootEvent('userChange', () =>
    syncHostSnapshots(session, options, dependencies)
  );
  const unbindFeatureFlagsLoaded = dependencies.onRootEvent(
    'featureFlagsLoaded',
    () => syncHostSnapshots(session, options, dependencies)
  );
  const unbindEagreements = dependencies.onRootEvent(
    'eagreementsGuardStateChanged',
    () => syncEagreementsSnapshot(session, dependencies)
  );
  scope.$on('$destroy', () => {
    unbindUserChange();
    unbindFeatureFlagsLoaded();
    unbindEagreements();
    dependencies.deactivateEagreements();
  });
}

function showBookSpotForEvent(
  dependencies: BookingPurchaseHostDependencies,
  args: {
    event: any;
    dismissModal: () => void;
    bookingId: string;
    guests: number;
  }
) {
  return dependencies
    .showModal({
      templateUrl: 'dialogs/book-spot/book-spot.html',
      controller: 'BookSpotController',
      controllerAs: 'vm',
      inputs: {
        bookSpotContext: {
          event: args.event,
          bookingId: args.bookingId,
          guests: args.guests,
        },
      },
    })
    .then((modal) => {
      modal.element.modal();
      args.dismissModal();
    })
    .catch((error) => {
      dependencies.logError?.();
      throw error;
    });
}

export function createBookingPurchaseHost(
  dependencies: BookingPurchaseHostDependencies
) {
  function create(scope: Scope, options: HostOptions): BookEventHostData {
    const session: HostSession = {
      user: dependencies.getCurrentUser(),
      branch: dependencies.getCurrentBranch(),
      data: null,
    };
    const bridge = digestBridge(
      dependencies.createReactDigestBridge(scope)
    );
    dependencies.activateEagreements();
    session.data = buildHostData(session, options, dependencies, bridge);
    subscribeHost(scope, session, options, dependencies);
    return session.data;
  }

  function createBookEvent(
    scope: Scope,
    event: any,
    dismissModal: () => void
  ) {
    return create(scope, {
      variant: 'book-event',
      event,
      dismissModal,
      showBookSpot: (bookingId, guests) =>
        showBookSpotForEvent(dependencies, {
          event,
          dismissModal,
          bookingId,
          guests,
        }),
    });
  }

  function createAppointment(
    scope: Scope,
    event: any,
    dismissModal: () => void
  ) {
    return create(scope, {
      variant: 'book-appointment',
      event,
      dismissModal,
    });
  }

  return { create, createBookEvent, createAppointment };
}
